import { RecordingStatus } from '@littlebird/sdk';

import { MicrophoneButton } from '@/components/blocks/Thread/MicrophoneButton';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';
import { useSpeechToText } from '@/features/speech-input/use-speech-to-text';
import { cn } from '@/lib/utils';

import type { ReactNode } from 'react';

export type SaveableFieldProps = {
  id: string;
  value: string;
  dirty: boolean;
  /** Blocks Save while leaving Discard usable, e.g. for invalid content. */
  saveDisabled?: boolean;
  /** Disables editing and both actions, e.g. while an async save runs. */
  busy?: boolean;
  placeholder?: string;
  className?: string;
  wrapperClassName?: string;
  onSave: () => void;
  onDiscard: () => void;
  onChange: (value: string) => void;
  statusContent?: ReactNode;
  enableVoiceInput?: boolean;
};

export const SaveableField = ({
  id,
  value,
  dirty,
  saveDisabled = false,
  busy = false,
  placeholder,
  className = 'text-sm resize-none',
  wrapperClassName,
  onSave,
  onDiscard,
  onChange,
  statusContent,
  enableVoiceInput = false,
}: SaveableFieldProps) => {
  const handleTranscript = (transcript: string) => {
    onChange(value ? `${value} ${transcript}` : transcript);
  };

  const {
    recordingStatus,
    startRecording,
    stopRecording,
    cancelRecording,
    mediaStreamRef,
  } = useSpeechToText(handleTranscript);

  const isSpeechInputActive = recordingStatus !== RecordingStatus.None;

  return (
    <YStack
      className={cn(
        'w-full mt-2 rounded-lg border border-border-strong focus-within:border-accent overflow-hidden bg-surface',
        wrapperClassName
      )}
    >
      <div className="relative flex flex-col flex-1 min-h-0">
        <TextArea
          id={id}
          className={cn(
            className,
            'rounded-none border-0 shadow-none focus-visible:border-0',
            enableVoiceInput && 'pr-12'
          )}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isSpeechInputActive || busy}
        />
        {enableVoiceInput && (
          <div className="absolute bottom-2 right-2">
            <MicrophoneButton
              mediaStreamRef={mediaStreamRef}
              recordingStatus={recordingStatus}
              onStartRecording={startRecording}
              onStopRecording={stopRecording}
              onCancelRecording={cancelRecording}
              tooltipSide="top"
              enableHotkeys={false}
            />
          </div>
        )}
      </div>
      <XStack className="w-full gap-2 px-3 py-2 justify-between items-center border-t border-border-strong bg-surface-hover">
        <XStack className="gap-2 items-center">
          {statusContent ?? (
            <span className="text-xs text-foreground-muted">
              {dirty ? 'Unsaved changes' : 'No unsaved changes'}
            </span>
          )}
        </XStack>
        <XStack className="gap-2">
          <Button
            variant="outline"
            size="dense"
            onClick={onDiscard}
            disabled={!dirty || isSpeechInputActive || busy}
          >
            Discard
          </Button>
          <Button
            variant="default"
            size="dense"
            onClick={onSave}
            disabled={!dirty || isSpeechInputActive || saveDisabled || busy}
          >
            Save
          </Button>
        </XStack>
      </XStack>
    </YStack>
  );
};
